// src/pages/Listing.jsx
import React, { useEffect, useState, useContext } from "react";
import { Helmet } from 'react-helmet-async';
import { AuthContext } from "../../context/AuthContext";
import FarmerAuthModal from "../farmers/modal/FarmerAuthModal";
import FarmerMyOrdersModal from "../farmers/modal/FarmerMyOrdersModal";

// --- Skeleton Loader ---
const ProductSkeleton = () => (
  <div className="bg-white rounded-lg overflow-hidden shadow-md animate-pulse h-72">
    <div className="w-full h-40 bg-gray-200"></div>
    <div className="p-4 space-y-2">
      <div className="h-5 bg-gray-300 w-3/4 rounded"></div>
      <div className="h-4 bg-gray-200 w-1/2 rounded"></div>
      <div className="h-4 bg-gray-200 w-full rounded"></div>
    </div>
  </div>
);

const API_URL = process.env.REACT_APP_API_URL;

// --- Currency Helper ---
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return "KShs. 0";
  return `KShs. ${Number(amount).toLocaleString()}`;
};

// --- Product Details Modal Component ---
const ProductDetailsModal = ({ product, isOpen, onClose, onOrder, user, isAuthenticated }) => {
  if (!isOpen || !product) return null;

  // Calculate discount percentage
  const calculateDiscountPercent = (price, discountedPrice) => {
    if (!price || !discountedPrice || price <= discountedPrice) return 0;
    return Math.round(((price - discountedPrice) / price) * 100);
  };

  const discountPercent = calculateDiscountPercent(product.price, product.discountedPrice);

  // Calculate days left until expiry
  const getDaysLeft = (expiryDateString) => {
    const today = new Date();
    const expiry = new Date(expiryDateString);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysLeft = getDaysLeft(product.expiry);
  const isExpiringSoon = daysLeft <= 7 && daysLeft > 0;
  const isExpired = daysLeft < 0;

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleOrderClick = () => {
    onClose();
    onOrder(product);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-4xl mx-auto max-h-[95vh] overflow-y-auto shadow-2xl transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 p-6 rounded-t-2xl z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 font-heading tracking-tight">{product.name}</h2>
              <div className="flex items-center gap-2 mt-2">
                <span className="bg-secondary-light text-secondary-dark px-3 py-1 rounded-full text-sm font-semibold font-accent">
                  {product.category}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-earth font-medium font-subheading">{product.type}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-neutral-100 w-10 h-10 rounded-full flex items-center justify-center text-2xl transition-all duration-200 ml-4"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Product Image */}
            <div className="space-y-6">
              <div className="relative group">
                <div className="overflow-hidden rounded-xl shadow-lg">
                  <img
                    src={product.image || "https://via.placeholder.com/600x400/D1D5DB/4B5563?text=Product+Image"}
                    alt={product.name}
                    className="w-full h-80 object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {discountPercent > 0 && (
                    <span className="bg-accent text-earth font-bold px-4 py-2 rounded-full shadow-lg font-accent text-sm animate-pulse">
                      🔥 -{discountPercent}% OFF
                    </span>
                  )}
                  {isExpiringSoon && (
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold text-white shadow-lg font-accent ${daysLeft <= 3 ? "bg-red-500 animate-bounce" : "bg-orange-500"
                      }`}>
                      ⏰ {daysLeft <= 3 ? `${daysLeft} DAYS LEFT!` : "Expiring Soon"}
                    </span>
                  )}
                  {product.quantity <= 5 && product.quantity > 0 && (
                    <span className="bg-yellow-500 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg font-accent">
                      ⚠️ Low Stock
                    </span>
                  )}
                  {product.quantity === 0 && (
                    <span className="bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg font-accent">
                      ❌ Out of Stock
                    </span>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleOrderClick}
                  disabled={!isAuthenticated || isExpired || product.quantity === 0}
                  className="flex-1 bg-primary text-white py-4 px-6 rounded-xl hover:bg-primary-light transform hover:scale-105 transition-all duration-200 font-semibold font-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100 flex items-center justify-center gap-3 shadow-lg"
                >
                  {isExpired ? 'Expired' : product.quantity === 0 ? 'Out of Stock' : 'Quick Request'}
                </button>
                {!isAuthenticated && (
                  <button
                    onClick={onClose}
                    className="flex-1 bg-primary text-white py-4 px-6 rounded-xl hover:bg-primary/90 transform hover:scale-105 transition-all duration-200 font-semibold font-accent shadow-lg"
                  >
                    Sign In to Order
                  </button>
                )}
              </div>
            </div>

            {/* Right Column - Product Details */}
            <div className="space-y-6">
              {/* Pricing Card */}
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 border border-primary/10">
                <h3 className="font-semibold text-gray-900 text-xl mb-4 font-heading flex items-center gap-2">
                  Pricing Details
                </h3>
                <div className="space-y-3">
                  {discountPercent > 0 ? (
                    <>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-sans">Original Price:</span>
                        <span className="text-lg text-red-400 line-through font-sans">
                          {formatCurrency(product.price)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 bg-white rounded-lg px-3 shadow-sm">
                        <span className="text-gray-700 font-semibold font-sans">Discounted Price:</span>
                        <span className="text-xl font-bold text-primary font-heading">
                          {formatCurrency(product.discountedPrice)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-green-600 font-semibold font-sans bg-green-50 rounded-lg px-3 py-2">
                        <span className="flex items-center gap-1">
                          You Save:
                        </span>
                        <span>{formatCurrency(product.price - product.discountedPrice)} ({discountPercent}%)</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center py-2 bg-white rounded-lg px-3 shadow-sm">
                      <span className="text-gray-700 font-semibold font-sans">Price:</span>
                      <span className="text-2xl font-bold text-primary font-heading">
                        {formatCurrency(product.price)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-xl font-heading flex items-center gap-2">
                  Product Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-xl border-2 transition-all duration-200 ${product.quantity === 0 ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200 hover:border-blue-300'
                    }`}>
                    <p className="text-[12px] text-blue-600 font-medium font-sans flex items-center gap-1">
                      Available Quantity
                    </p>
                    <p className={`text-[14px] font-bold font-heading mt-1 ${product.quantity === 0 ? 'text-red-800' : 'text-blue-800'
                      }`}>
                      {product.quantity} {product.unit}
                    </p>
                    {product.quantity === 0 && (
                      <p className="text-xs text-red-600 mt-1 font-sans">Currently unavailable</p>
                    )}
                  </div>

                  <div className="bg-green-50 border-2 border-green-200 p-4 rounded-xl hover:border-green-300 transition-all duration-200">
                    <p className="text-[12px] text-green-600 font-medium font-sans flex items-center gap-1">
                      Category
                    </p>
                    <p className="text-[14px] font-bold text-green-800 font-heading mt-1">{product.category}</p>
                  </div>

                  <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-xl hover:border-purple-300 transition-all duration-200">
                    <p className="text-[12px] text-purple-600 font-medium font-sans flex items-center gap-1">
                      Type
                    </p>
                    <p className="text-[14px] font-bold text-purple-800 font-heading mt-1">{product.type}</p>
                  </div>

                  <div className={`p-4 rounded-xl border-2 transition-all duration-200 ${isExpired ? 'bg-red-50 border-red-200' : isExpiringSoon ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}>
                    <p className="text-[12px] text-gray-600 font-medium font-sans flex items-center gap-1">
                      <span>📅</span>
                      Expiry Date
                    </p>
                    <p className={`text-[14px] font-bold font-heading mt-1 ${isExpired ? 'text-red-800' : isExpiringSoon ? 'text-orange-800' : 'text-gray-800'
                      }`}>
                      {formatDate(product.expiry)}
                    </p>
                    {!isExpired && (
                      <p className={`text-xs mt-1 font-sans ${isExpiringSoon ? 'text-orange-600 font-semibold' : 'text-gray-600'
                        }`}>
                        {daysLeft} {daysLeft === 1 ? 'day' : 'days'} remaining
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-semibold text-gray-900 text-xl mb-3 font-heading flex items-center gap-2">
                  Description
                </h3>
                <p className="text-gray-700 leading-relaxed bg-neutral-50 p-4 rounded-xl border border-neutral-200 font-sans text-justify">
                  {product.description || "No detailed description available for this product."}
                </p>
              </div>

              {/* Eco-Friendly Impact */}
              <div className="bg-gradient-to-r from-secondary/10 to-primary/10 border border-secondary/20 rounded-2xl p-5">
                <h4 className="font-semibold text-secondary text-lg mb-3 font-heading flex items-center gap-2">
                  <span className="text-2xl">🌱</span>
                  Eco-Friendly Impact
                </h4>
                <p className="text-earth text-sm font-sans leading-relaxed">
                  By purchasing this surplus farm input, you're helping reduce agricultural waste and supporting sustainable farming practices in your community. Together, we're building a greener future! 🌍
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-neutral-200 p-6 rounded-b-2xl">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <div className="text-center lg:text-left">
              <p className="text-sm text-gray-600 font-sans flex items-center justify-center lg:justify-start gap-2">
                <span className="text-primary">💡</span>
                Need more information? Contact the seller directly after placing your order.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-8 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-neutral-50 hover:border-gray-400 transform hover:scale-105 transition-all duration-200 font-medium font-accent"
              >
                Close
              </button>
              <button
                onClick={handleOrderClick}
                disabled={!isAuthenticated || isExpired || product.quantity === 0}
                className="px-8 py-3 bg-primary text-white rounded-xl hover:bg-primary-light transform hover:scale-105 transition-all duration-200 font-semibold font-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:scale-100 flex items-center gap-3 shadow-lg"
              >
                Request Delivery
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Simple Modal Component ---
const SimpleModal = ({ isOpen, onClose, title, children, type = 'info' }) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            {getIcon()}
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-heading">
                {title}
              </h3>
              <div className="mt-2">
                <div className="text-sm text-gray-500 font-sans">
                  {children}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-accent sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Confirmation Modal Component ---
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-heading">
                {title}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500 font-sans">
                  {message}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 font-accent sm:ml-3 sm:w-auto sm:text-sm"
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              {confirmText}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-accent sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Corrected Order Modal with Proper Price Per Unit Calculation ---
const OrderModal = ({ product, onClose, onConfirm, user }) => {
  const [quantity, setQuantity] = useState(1);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");

  // Use the same unit information system from AddProduct for consistency
  const unitInformation = {
    'kg': { type: 'mass', baseUnit: 'g', factor: 1000, name: 'Kilograms' },
    'g': { type: 'mass', baseUnit: 'g', factor: 1, name: 'Grams' },
    'ton': { type: 'mass', baseUnit: 'kg', factor: 1000, name: 'Metric Tons' },
    'lbs': { type: 'mass', baseUnit: 'g', factor: 453.592, name: 'Pounds' },
    'oz': { type: 'mass', baseUnit: 'g', factor: 28.3495, name: 'Ounces' },
    'liter': { type: 'volume', baseUnit: 'ml', factor: 1000, name: 'Liters' },
    'ml': { type: 'volume', baseUnit: 'ml', factor: 1, name: 'Milliliters' },
    'gallon': { type: 'volume', baseUnit: 'ml', factor: 3785.41, name: 'Gallons' },
    'bushels': { type: 'volume', baseUnit: 'liter', factor: 35.2391, name: 'Bushels' },
    'bags': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Bags' },
    'packets': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Packets' },
    'sacks': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Sacks' },
    'bales': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Bales' },
    'units': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Units' },
    'pieces': { type: 'countable', baseUnit: 'pieces', factor: 1, name: 'Pieces' },
    'crates': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Crates' },
    'dozens': { type: 'countable', baseUnit: 'pieces', factor: 12, name: 'Dozens' },
    'bunches': { type: 'countable', baseUnit: 'units', factor: 1, name: 'Bunches' }
  };

  // Calculate max quantity based on available stock
  const maxQuantity = Math.floor(product.quantity);

  // CORRECTED: Calculate price per unit (e.g., Kshs. 10/kg)
  const calculatePricePerUnit = () => {
    // Use discounted price per base unit if available, otherwise regular price per base unit
    if (product.discountedPricePerBaseUnit > 0) {
      return product.discountedPricePerBaseUnit;
    }
    return product.pricePerBaseUnit || (product.price / product.quantity);
  };

  // CORRECTED: Calculate total price = price per unit × quantity ordered
  const calculateTotalPrice = () => {
    const pricePerUnit = calculatePricePerUnit();
    const quantityInBaseUnits = convertQuantityToBaseUnits(quantity, product.unit, product.baseUnit);
    return pricePerUnit * quantityInBaseUnits;
  };

  // Convert selected quantity to base units for proper calculation
  const convertQuantityToBaseUnits = (qty, fromUnit, toBaseUnit) => {
    if (fromUnit === toBaseUnit) return qty;

    const fromInfo = unitInformation[fromUnit];
    const toInfo = unitInformation[toBaseUnit];

    if (!fromInfo || !toInfo || fromInfo.type !== toInfo.type) {
      console.warn(`Cannot convert ${fromUnit} to ${toBaseUnit}, using 1:1 ratio`);
      return qty;
    }

    // Simple conversion through base units
    if (fromUnit !== fromInfo.baseUnit) {
      const inFromBase = qty * fromInfo.factor;
      if (fromInfo.baseUnit !== toInfo.baseUnit) {
        // Handle cross-base unit conversions
        if (fromInfo.baseUnit === 'g' && toInfo.baseUnit === 'kg') {
          return inFromBase / 1000;
        } else if (fromInfo.baseUnit === 'kg' && toInfo.baseUnit === 'g') {
          return inFromBase * 1000;
        } else if (fromInfo.baseUnit === 'ml' && toInfo.baseUnit === 'liter') {
          return inFromBase / 1000;
        } else if (fromInfo.baseUnit === 'liter' && toInfo.baseUnit === 'ml') {
          return inFromBase * 1000;
        }
      }
      return inFromBase;
    }

    return qty * fromInfo.factor;
  };

  const pricePerUnit = calculatePricePerUnit();
  const totalPrice = calculateTotalPrice();

  // Calculate savings if discounted
  const calculateSavings = () => {
    if (!product.discountedPricePerBaseUnit || product.discountedPricePerBaseUnit >= product.pricePerBaseUnit) return null;

    const regularPricePerUnit = product.pricePerBaseUnit;
    const discountedPricePerUnit = product.discountedPricePerBaseUnit;
    const quantityInBaseUnits = convertQuantityToBaseUnits(quantity, product.unit, product.baseUnit);

    const regularTotal = regularPricePerUnit * quantityInBaseUnits;
    const discountedTotal = discountedPricePerUnit * quantityInBaseUnits;
    const savings = regularTotal - discountedTotal;
    const savingsPercent = ((savings / regularTotal) * 100).toFixed(1);

    return {
      amount: savings,
      percent: savingsPercent
    };
  };

  const savings = calculateSavings();

  // Get unit information for display
  const getUnitInfo = (unit) => {
    return unitInformation[unit] || { name: unit, type: 'countable' };
  };

  const handleSubmit = () => {
    if (!deliveryAddress.trim()) {
      onConfirm(product, quantity, pricePerUnit, totalPrice, deliveryAddress, specialInstructions, true);
      return;
    }
    onConfirm(product, quantity, pricePerUnit, totalPrice, deliveryAddress, specialInstructions, false);
  };

  // Info tooltip for order modal
  const InfoTip = ({ content }) => (
    <div className="relative inline-block ml-1 group">
      <div className="inline-flex items-center justify-center w-4 h-4 bg-gray-200 rounded-full cursor-help text-xs text-gray-600">
        ?
      </div>
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 w-48 text-center z-10">
        {content}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );

  // Format price per unit for display
  const formatPricePerUnit = () => {
    return `${formatCurrency(pricePerUnit)} / ${product.baseUnit || product.unit}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl mx-auto max-h-[95vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 font-heading">Confirm Your Delivery Request</h3>
              <p className="text-sm sm:text-base text-gray-600 mt-1 font-sans">
                Hello, {user?.name}! Review your order details below
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl sm:text-3xl ml-4 transition-colors duration-200"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Eco Message */}
          <div className="bg-primary bg-opacity-10 border border-primary border-opacity-20 rounded-lg p-4">
            <div className="flex items-center text-primary text-sm sm:text-base font-sans">
              <span className="mr-2 text-lg">🌱</span>
              You're helping reduce farm input waste by using surplus products!
            </div>
          </div>

          {/* Product Info */}
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <img
              src={product.image || "https://via.placeholder.com/80x80/D1D5DB/4B5563?text=Product"}
              alt={product.name}
              className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-lg sm:text-xl truncate font-heading">{product.name}</h4>
              <p className="text-sm sm:text-base text-gray-600 font-sans">{product.category}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-sans">
                  {getUnitInfo(product.unit).name}
                </span>
                {product.discountedPricePerBaseUnit > 0 && (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-sans">
                    {calculateSavings()?.percent}% OFF
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-500 mt-2 font-sans">
                Available: {product.quantity} {product.unit}
                {product.baseUnit && product.baseUnit !== product.unit && (
                  <span className="ml-2 text-gray-400">
                    (Base unit: {product.baseUnit})
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Form Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Form Fields */}
            <div className="space-y-6">
              {/* Quantity Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center font-sans">
                  How many {product.unit} do you need?
                  <InfoTip content={`Select how many ${getUnitInfo(product.unit).name} you want to order. Maximum available: ${maxQuantity}`} />
                </label>
                <select
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-base font-sans"
                >
                  {[...Array(Math.min(maxQuantity, 100))].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} {product.unit}
                      {i + 1 === maxQuantity && " (Max available)"}
                      {i + 1 > maxQuantity * 0.8 && i + 1 !== maxQuantity && " (High demand)"}
                    </option>
                  ))}
                </select>
                {maxQuantity > 100 && (
                  <p className="text-xs text-gray-500 mt-1 font-sans">
                    Showing first 100 options. Contact seller for larger quantities.
                  </p>
                )}
              </div>

              {/* Delivery Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center font-sans">
                  Delivery Address *
                  <InfoTip content="Please provide your complete delivery address including county, town, and specific location for accurate delivery" />
                </label>
                <textarea
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Enter your complete delivery address including county, town, and specific location"
                  rows="4"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none text-base font-sans"
                  required
                />
              </div>

              {/* Special Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center font-sans">
                  Special Instructions (Optional)
                  <InfoTip content="Add any special delivery instructions, preferred delivery times, or notes for the seller" />
                </label>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Any special delivery instructions, preferred delivery times, or notes for the seller"
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none text-base font-sans"
                />
              </div>
            </div>

            {/* Right Column - CORRECTED Order Summary */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-900 text-lg mb-4 flex items-center font-heading">
                  Order Summary
                  <InfoTip content="Price calculation: Total = Price per unit × Quantity ordered" />
                </h4>

                <div className="space-y-4">
                  {/* Price Per Unit */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex justify-between items-center text-sm sm:text-base">
                      <span className="text-gray-600 font-medium font-sans">Price per {product.baseUnit || product.unit}:</span>
                      <span className="font-bold text-primary font-heading">{formatPricePerUnit()}</span>
                    </div>
                    {product.discountedPricePerBaseUnit > 0 && (
                      <div className="flex justify-between items-center text-xs text-gray-500 mt-1 font-sans">
                        <span>Original price:</span>
                        <span className="line-through">
                          {formatCurrency(product.pricePerBaseUnit)} / {product.baseUnit || product.unit}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Quantity Breakdown */}
                  <div className="flex justify-between items-center text-sm sm:text-base">
                    <span className="text-gray-600 font-sans">Quantity:</span>
                    <span className="font-medium text-gray-900 font-sans">
                      {quantity} {product.unit}
                      {product.baseUnit && product.baseUnit !== product.unit && (
                        <div className="text-xs text-gray-500 font-sans">
                          = {convertQuantityToBaseUnits(quantity, product.unit, product.baseUnit).toFixed(2)} {product.baseUnit}
                        </div>
                      )}
                    </span>
                  </div>

                  {/* Calculation Breakdown */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-xs text-blue-700 font-medium mb-1 font-sans">Calculation:</div>
                    <div className="text-xs text-blue-600 space-y-1 font-sans">
                      <div>
                        {pricePerUnit.toFixed(4)} × {convertQuantityToBaseUnits(quantity, product.unit, product.baseUnit).toFixed(2)} {product.baseUnit}
                      </div>
                      <div className="font-semibold">
                        = {formatCurrency(totalPrice)}
                      </div>
                    </div>
                  </div>

                  {/* Savings Display */}
                  {savings && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-700 font-medium font-sans">You Save:</span>
                        <div className="text-right">
                          <span className="text-green-700 font-bold font-heading">{formatCurrency(savings.amount)}</span>
                          <div className="text-xs text-green-600 font-sans">({savings.percent}% discount)</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Total Amount */}
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between items-center text-lg font-semibold">
                      <span className="text-gray-900 font-heading">Total Amount:</span>
                      <div className="text-right">
                        <span className="text-primary text-xl font-heading">{formatCurrency(totalPrice)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-4 space-y-2">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs sm:text-sm text-blue-700 font-sans">
                      💡 Your request creates an automatic match. The seller will contact you to confirm delivery details and finalize the order.
                    </p>
                  </div>

                  {/* Stock Level Indicator */}
                  <div className="p-2 bg-amber-50 rounded border border-amber-200">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-amber-700 font-sans">Stock Level:</span>
                      <span className={`font-medium font-sans ${product.quantity > 20 ? 'text-green-600' :
                        product.quantity > 5 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                        {product.quantity > 20 ? 'High' : product.quantity > 5 ? 'Medium' : 'Low'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className={`h-1.5 rounded-full ${product.quantity > 20 ? 'bg-green-500' :
                          product.quantity > 5 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                        style={{
                          width: `${Math.min((product.quantity / Math.max(product.quantity, 30)) * 100, 100)}%`
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-medium text-base sm:text-[13px] font-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!deliveryAddress.trim()}
              className="flex-1 py-4 px-6 bg-primary text-white text-[12px] rounded-lg hover:bg-primary-light transition font-semibold font-accent flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-[13px]"
            >
              Send Delivery Request
            </button>
          </div>

          {/* Validation Note */}
          {!deliveryAddress.trim() && (
            <p className="text-sm text-red-600 text-center font-sans">
              Please enter your delivery address to continue
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Updated LogoutIcon with loading state
const LogoutIcon = ({ onLogout, loading = false }) => (
  <button
    onClick={onLogout}
    disabled={loading}
    className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors duration-200 p-2 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed font-accent"
    title="Logout"
  >
    {loading ? (
      <svg className="animate-spin h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    ) : (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
        />
      </svg>
    )}
    <span className="text-sm font-medium hidden sm:block">
      {loading ? 'Logging out...' : 'Logout'}
    </span>
  </button>
);

// Orders Icon Component
const OrdersIcon = ({ onClick, orderCount = 0 }) => (
  <button
    onClick={onClick}
    className="relative flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors duration-200 p-2 rounded-lg hover:bg-green-50 font-accent"
    title="View Orders"
  >
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
      />
    </svg>
    <span className="text-sm font-medium hidden sm:block">Orders</span>
    {orderCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-sans">
        {orderCount}
      </span>
    )}
  </button>
);

// --- Sign In Prompt Component ---
const SignInPrompt = ({ onSignIn }) => (
  <button
    onClick={onSignIn}
    className="bg-primary text-white text-[12px] px-4 py-2 rounded-lg hover:bg-primary-light transition font-semibold font-accent whitespace-nowrap flex items-center gap-2"
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
    </svg>
    Sign In / Register
  </button>
);

// --- Main Listing Component ---
const Listing = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMyOrdersModal, setShowMyOrdersModal] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [authModalMode, setAuthModalMode] = useState('login'); // 'login', 'register', 'verify', 'forgot-password'

  // Modal states
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  });

  // Use the auth context
  const authContext = useContext(AuthContext);

  // Safe destructuring with fallbacks
  const {
    isAuthenticated = false,
    token = null,
    user = null,
    userType = null,
    logout
  } = authContext || {};

  // Determine if user is a farmer
  const isFarmer = userType === 'farmer';

  // Helper: check expiry and get days left
  const getDaysLeft = (expiryDateString) => {
    const today = new Date();
    const expiry = new Date(expiryDateString);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isExpired = (expiryDateString) => {
    return getDaysLeft(expiryDateString) < 0;
  };

  // FIXED: Enhanced URL parameter handling WITHOUT premature cleaning
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verifyFarmer = urlParams.get('verifyFarmer');
    const resetFarmerPassword = urlParams.get('resetFarmerPassword');
    const token = urlParams.get('token');
    
    console.log('🔍 Checking URL parameters:', {
      verifyFarmer,
      resetFarmerPassword,
      token: token ? `${token.substring(0, 10)}...` : 'No token',
      fullURL: window.location.href
    });

    // Handle email verification - NO CLEANUP HERE
    if (verifyFarmer === 'true' && token) {
      console.log('🔐 Opening auth modal for email verification');
      setShowAuthModal(true);
      setAuthModalMode('verify');
      
      // REMOVED: URL cleaning - let FarmerAuthModal handle it
      console.log('⏳ URL parameters preserved for FarmerAuthModal');
    }
    
    // Handle password reset - NO CLEANUP HERE  
    if (resetFarmerPassword === 'true' && token) {
      console.log('🔑 Opening auth modal for password reset');
      setShowAuthModal(true);
      setAuthModalMode('forgot-password');
      
      // REMOVED: URL cleaning - let FarmerAuthModal handle it
      console.log('⏳ URL parameters preserved for FarmerAuthModal');
    }
  }, []);

  // Modal helper functions
  const showModal = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type });
  };

  const showConfirmationModal = (title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel') => {
    setConfirmationModal({ isOpen: true, title, message, onConfirm, confirmText, cancelText });
  };

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({ ...confirmationModal, isOpen: false });
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products`, {
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.message || "Failed to fetch products");
        }

        const data = await res.json();

        // Filter products: only non-expired AND with stock greater than zero
        const availableProducts = data.filter(
          (product) => !isExpired(product.expiry) && product.quantity > 0
        );

        setProducts(availableProducts);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const calculateDiscountPercent = (price, discountedPrice) => {
    if (!price || !discountedPrice || price <= discountedPrice) return 0;
    return Math.round(((price - discountedPrice) / price) * 100);
  };

  const isExpiringSoon = (expiryDateString) => {
    const daysLeft = getDaysLeft(expiryDateString);
    return daysLeft <= 7 && daysLeft > 0;
  };

  const handleQuickOrder = (product) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      setAuthModalMode('login');
      return;
    }

    // Check if product is out of stock
    if (product.quantity === 0) {
      showModal('Out of Stock', 'This product is currently out of stock. Please check back later or browse other available products.', 'warning');
      return;
    }

    setSelectedProduct(product);
  };

  const handleViewDetails = (product) => {
    setSelectedProductDetails(product);
    setShowProductDetails(true);
  };

  const handleCloseDetails = () => {
    setShowProductDetails(false);
    setSelectedProductDetails(null);
  };

  const handleOrderFromDetails = (product) => {
    setShowProductDetails(false);
    setSelectedProductDetails(null);
    handleQuickOrder(product);
  };

  const handleConfirmOrder = async (product, quantity, unitPrice, totalPrice, deliveryAddress, specialInstructions, hasValidationError = false) => {
    if (hasValidationError) {
      showModal('Missing Information', 'Please enter your delivery address to continue.', 'warning');
      return;
    }

    // Additional check for zero stock
    if (product.quantity === 0) {
      showModal('Out of Stock', 'This product is no longer available. It may have been sold out recently.', 'error');
      setSelectedProduct(null);
      return;
    }

    setOrdering(true);
    try {
      console.log('Attempting to connect to:', `${API_URL}/api/delivery-requests`);

      const response = await fetch(`${API_URL}/api/delivery-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: product._id,
          quantity,
          unitPrice,
          totalPrice,
          deliveryAddress,
          specialInstructions: specialInstructions || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create delivery request");
      }

      const result = await response.json();

      // Success message
      showModal(
        'Delivery Request Sent!',
        `Thank you, ${user?.name}! Your delivery request for ${quantity} ${product.unit} of ${product.name} has been sent successfully. A match has been automatically created and the seller will contact you shortly.`,
        'success'
      );

      setSelectedProduct(null);

    } catch (error) {
      console.error("Delivery request error:", error);

      if (error.message === 'Failed to fetch') {
        showModal(
          'Connection Error',
          'Cannot connect to server. Please check if the backend is running on port 5000.',
          'error'
        );
      } else {
        showModal(
          'Request Failed',
          `Sorry, there was an issue with your delivery request: ${error.message}`,
          'error'
        );
      }
    } finally {
      setOrdering(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowAuthModal(false);
    setAuthModalMode('login');
    window.location.reload();
  };

  const handleMakeRequest = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      setAuthModalMode('login');
    } else {
      showModal(
        'Coming Soon',
        'Demand form feature is coming soon! Stay tuned for updates.',
        'info'
      );
    }
  };

  const handleViewOrders = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      setAuthModalMode('login');
      return;
    }

    // Only farmers should see "My Orders"
    if (isFarmer) {
      setShowMyOrdersModal(true);
      return;
    }

    // If non-farmers have no modal to show, just ignore or handle future logic
    console.warn("No order modal available for this user type.");
  };

  const handleLogout = async () => {
    showConfirmationModal(
      'Confirm Logout',
      'Are you sure you want to logout?',
      async () => {
        setLogoutLoading(true);
        try {
          if (logout) {
            await logout();
          } else {
            localStorage.removeItem('auth');
            window.location.reload();
          }
        } catch (error) {
          console.error("Logout error:", error);
          localStorage.removeItem('auth');
          window.location.reload();
        } finally {
          setLogoutLoading(false);
        }
      },
      'Logout',
      'Cancel'
    );
  };

  // --- Reusable product grid ---
  const ProductGrid = ({ items }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((product) => {
        const discountPercent = calculateDiscountPercent(
          product.price,
          product.discountedPrice
        );
        const expiring = isExpiringSoon(product.expiry);
        const daysLeft = getDaysLeft(product.expiry);

        return (
          <div
            key={product._id}
            className="bg-white rounded-lg shadow-md overflow-hidden transform hover:scale-[1.01] transition duration-200 ease-in-out border border-gray-100 relative"
          >
            {/* Discount Badge */}
            {discountPercent > 0 && (
              <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow font-sans">
                -{discountPercent}% OFF
              </span>
            )}

            {/* Expiry / Stock Badge */}
            {(expiring || product.quantity <= 5) && (
              <div
                className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[11px] font-semibold text-white shadow font-sans ${expiring
                  ? daysLeft <= 3 ? "bg-red-500" : "bg-orange-500"
                  : product.quantity === 0 ? "bg-red-600" : "bg-yellow-500"
                  }`}
              >
                {expiring
                  ? daysLeft <= 3 ? `${daysLeft} DAYS LEFT!` : "Expiring Soon"
                  : product.quantity === 0 ? "Out of Stock" : "Low Stock"
                }
              </div>
            )}

            <img
              src={
                product.image ||
                "https://via.placeholder.com/400x300/D1D5DB/4B5563?text=Product"
              }
              alt={product.name}
              className="w-full h-40 object-cover object-center cursor-pointer"
              onClick={() => handleViewDetails(product)}
            />

            <div className="p-4">
              <h2
                className="font-semibold text-lg text-gray-900 line-clamp-1 cursor-pointer hover:text-primary transition-colors font-heading"
                onClick={() => handleViewDetails(product)}
              >
                {product.name}
              </h2>

              {/* Category & Type Badges */}
              <div className="flex gap-2 mt-1 mb-2">
                <span className="text-[11px] font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full font-sans">
                  {product.category}
                </span>
                <span className="text-[11px] font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full font-sans">
                  {product.type}
                </span>
              </div>

              <p className="text-sm text-gray-600 mb-2 line-clamp-2 font-sans">
                {product.description || "No description provided."}
              </p>

              {/* Pricing */}
              <div className="flex flex-col items-end justify-between border-t pt-2">
                <div>
                  {discountPercent > 0 ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-red-400 line-through font-sans">
                        {formatCurrency(product.price)}
                      </span>
                      <span className="text-base font-bold text-primary font-heading">
                        {formatCurrency(product.discountedPrice)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-base font-bold text-gray-800 font-heading">
                      {formatCurrency(product.price)}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 w-full mt-3">
                  <button
                    className="flex-1 bg-gray-200 text-gray-700 text-sm font-semibold py-2 rounded-lg hover:bg-gray-300 transition font-accent"
                    onClick={() => handleViewDetails(product)}
                  >
                    View Details
                  </button>
                  <button
                    className={`flex-1 text-sm font-semibold py-2 rounded-lg transition font-accent ${isAuthenticated && product.quantity > 0
                      ? "bg-primary text-white hover:bg-primary-light"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    onClick={() => handleQuickOrder(product)}
                    disabled={ordering || product.quantity === 0}
                  >
                    {ordering ? "Sending..." :
                      !isAuthenticated ? "Sign In to Request" :
                        product.quantity === 0 ? "Out of Stock" : "🚚 Quick Request"}
                  </button>
                </div>
              </div>

              {/* Stock & Expiry */}
              <div className="mt-2 text-xs text-gray-500 space-y-1 font-sans">
                <p>
                  <span className="font-semibold">Available:</span>{" "}
                  {product.quantity} {product.unit}
                  {product.quantity === 0 && (
                    <span className="ml-1 text-red-600 font-semibold">(Out of Stock)</span>
                  )}
                </p>
                <p>
                  <span className="font-semibold">Expires:</span>{" "}
                  {new Date(product.expiry).toLocaleDateString("en-GB")}
                  {expiring && (
                    <span className="ml-1 text-orange-600 font-semibold">
                      ({daysLeft} days left)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // SEO metadata
  const pageTitle = `Agricultural Inputs Marketplace - ${products.length} Products Available | Croper`;
  const pageDescription = `Browse ${products.length} agricultural inputs and farm products. Connect with farmers and agribusinesses to reduce agricultural waste. Sustainable farming solutions in Kenya.`;
  const canonicalUrl = `https://cropser.com/`;

  // --- RENDERING ---
  if (error)
    return (
      <>
        <Helmet>
          <title>Error Loading Marketplace | CropSer</title>
          <meta name="description" content="Unable to load agricultural products. Please try again later." />
        </Helmet>
        <div className="p-6 text-center mt-10">
          <h2 className="text-2xl font-bold text-red-600 mb-2 font-heading">
            Error Loading Data 😔
          </h2>
          <p className="text-gray-600 font-sans">{error}</p>
        </div>
      </>
    );

  // Group products by category dynamically
  const categories = [...new Set(products.map((p) => p.category))];

  return (
    <>
      {/* SEO Head Section */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CropSer" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="agricultural inputs, farm products, sustainable farming, Kenya agriculture, crop inputs, farming supplies" />

        {/* Structured Data for Product Listings */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Agricultural Inputs Marketplace",
            "description": pageDescription,
            "numberOfItems": products.length,
            "itemListElement": products.slice(0, 10).map((product, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Product",
                "name": product.name,
                "description": product.description,
                "category": product.category,
                "image": product.image,
                "offers": {
                  "@type": "Offer",
                  "price": product.discountedPrice || product.price,
                  "priceCurrency": "KES",
                  "availability": product.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
                }
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header with user info if logged in */}
        <header className="flex justify-between items-center mb-6 border-b pb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800 font-heading">
              Community Inputs Hub
            </h1>
            <p className="text-sm text-gray-600 mt-1 font-sans">
              Help reduce agricultural waste - request inputs at great prices
            </p>
          </div>
          <div className="flex gap-4 items-center">
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-[12px] text-gray-600 font-sans">Welcome back,</p>
                  <p className="font-semibold text-primary text-[14px] font-heading">{user?.name}</p>
                  {/* <p className="text-xs text-gray-500 mt-1 font-sans">
                    {isFarmer ? 'Farmer Account' : userType === 'agribusiness' ? 'Agribusiness Account' : 'Account'}
                  </p> */}
                </div>
                <div className="border-l border-gray-300 h-8"></div>
                <OrdersIcon onClick={handleViewOrders} />

                <button
                  onClick={handleMakeRequest}
                  className="text-primary text-[12px] font-semibold px-4 py-2 rounded-lg border border-primary hover:bg-primary hover:bg-opacity-10 transition font-accent"
                >
                  Make a Request
                </button>
                <LogoutIcon onLogout={handleLogout} loading={logoutLoading} />
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-600 hidden md:block font-sans">Sign in to request inputs</p>
                <SignInPrompt onSignIn={() => {
                  setShowAuthModal(true);
                  setAuthModalMode('login');
                }} />
              </div>
            )}
          </div>
        </header>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-lg">
            <p className="text-lg text-gray-500 font-medium font-sans">
              It looks like the marketplace is empty right now. Our sellers are updating their listings — please check again later!
            </p>
            {!isAuthenticated && (
              <button
                onClick={() => {
                  setShowAuthModal(true);
                  setAuthModalMode('register');
                }}
                className="mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-light transition font-semibold font-accent"
              >
                Join as Farmer to Get Notified
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-12">
            {categories.map((category) => (
              <section key={category}>
                <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-1 font-heading">
                  {category}
                </h2>
                <ProductGrid
                  items={products.filter((p) => p.category === category)}
                />
              </section>
            ))}
          </div>
        )}

        {/* Product Details Modal */}
        {selectedProductDetails && (
          <ProductDetailsModal
            product={selectedProductDetails}
            isOpen={showProductDetails}
            onClose={handleCloseDetails}
            onOrder={handleOrderFromDetails}
            user={user}
            isAuthenticated={isAuthenticated}
          />
        )}

        {/* Order Modal */}
        {selectedProduct && (
          <OrderModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            onConfirm={handleConfirmOrder}
            user={user}
          />
        )}

        {/* Enhanced Farmer Auth Modal with Security Features */}
        {showAuthModal && (
          <FarmerAuthModal
            onClose={() => {
              setShowAuthModal(false);
              setAuthModalMode('login');
            }}
            onLoginSuccess={handleLoginSuccess}
            initialMode={authModalMode}
          />
        )}

        {/* Farmer My Orders Modal (for farmers) */}
        {showMyOrdersModal && (
          <FarmerMyOrdersModal
            isOpen={showMyOrdersModal}
            onClose={() => setShowMyOrdersModal(false)}
          />
        )}

        {/* Simple Modal for alerts */}
        <SimpleModal
          isOpen={modal.isOpen}
          onClose={closeModal}
          title={modal.title}
          type={modal.type}
        >
          {modal.message}
        </SimpleModal>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          onClose={closeConfirmationModal}
          onConfirm={confirmationModal.onConfirm}
          title={confirmationModal.title}
          message={confirmationModal.message}
          confirmText={confirmationModal.confirmText}
          cancelText={confirmationModal.cancelText}
        />
      </div>
    </>
  );
};

export default Listing;